/**
 * AffTrackLayerSync (#802)
 *
 * The drawer-side half of drawing an AFF aircraft track. AffLayerProvider owns
 * the fetch, the colour choice and the track id, then announces the track; this
 * component turns that announcement into a registry layer, which is what
 * actually draws it and what gives it the drawer's visibility, opacity and
 * z-order controls.
 *
 * Mirrors FirePerimeterLayerSync: the module that draws never holds the React
 * context, and the component that holds the context never touches the map
 * directly.
 */

import React, { useEffect, useRef } from 'react';
import { useLayerDrawer } from '../layerDrawer/LayerDrawerContext';
import { affTrackLayerConfig } from '../layers2/AffTrackLayer';
import {
  AFF_TRACK_DRAWN_EVENT,
  AFF_TRACK_REMOVED_EVENT,
  AffTrackDrawnDetail,
  AffTrackRemovedDetail,
} from '../../components/explorers/affExplorer/affTrackEvent';
import { logger } from '../../utils/logger';

const log = logger('AffTrackLayerSync');

export const AffTrackLayerSync: React.FC = () => {
  const { registerTrackLayer, unregisterTrackLayer } = useLayerDrawer();

  // The listeners bind once. Reading the current callbacks through a ref keeps
  // them from going stale when the drawer's map or registry is replaced — the
  // exact trap that made tracks silently stop drawing in #815.
  const actionsRef = useRef({ registerTrackLayer, unregisterTrackLayer });
  useEffect(() => {
    actionsRef.current = { registerTrackLayer, unregisterTrackLayer };
  }, [registerTrackLayer, unregisterTrackLayer]);

  useEffect(() => {
    const onDrawn = (ev: Event) => {
      const detail = (ev as CustomEvent<AffTrackDrawnDetail>).detail;
      if (!detail?.trackId) return;

      const config = affTrackLayerConfig({
        trackId: detail.trackId,
        aircraftName: detail.aircraftName || detail.esn,
        data: detail.data,
        color: detail.color,
        halo: detail.halo,
      });
      config.metadata = { ...config.metadata, esn: detail.esn, span: detail.span };

      actionsRef.current.registerTrackLayer(config).catch((error) => {
        // Say what failed. A track that silently fails to register looks
        // identical to one that was never requested.
        log.error(`Could not register the drawer entry for track ${detail.trackId}`, error);
      });
    };

    const onRemoved = (ev: Event) => {
      const detail = (ev as CustomEvent<AffTrackRemovedDetail>).detail;
      if (!detail?.trackId) return;

      actionsRef.current.unregisterTrackLayer(detail.trackId).catch((error) => {
        log.error(`Could not remove the drawer entry for track ${detail.trackId}`, error);
      });
    };

    window.addEventListener(AFF_TRACK_DRAWN_EVENT, onDrawn);
    window.addEventListener(AFF_TRACK_REMOVED_EVENT, onRemoved);

    return () => {
      window.removeEventListener(AFF_TRACK_DRAWN_EVENT, onDrawn);
      window.removeEventListener(AFF_TRACK_REMOVED_EVENT, onRemoved);
    };
  }, []);

  return null;
};

export default AffTrackLayerSync;
